import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { load } from 'js-yaml';
import { map, Observable } from 'rxjs';

import { CelumPropertiesProvider } from '@celum/core';
import { SpecificationContract, SpecificationList } from '@celum/formats/domain';

import { FormatsContextAware } from './formats-context-aware';

/**
 * Single implementation of specification-related API calls shared by the standalone `formats` app and the
 * `formats-embed` app. The environment-specific concerns (apiUrl/tenant resolution, authentication headers for
 * embedded calls) are handled by `FormatsContextAware` based on the `FORMATS_CONTEXT` provided where this service
 * is registered.
 */
@Injectable()
export class SpecificationsService extends FormatsContextAware {
  private http = inject(HttpClient);

  public getSpecifications(): Observable<SpecificationList> {
    return this.withContext(context => this.http.get<SpecificationList>(`${this.apiUrl}/specifications`, { context }));
  }

  public getSpecificationContract(specificationKey: string): Observable<SpecificationContract> {
    const contractApiUrl = CelumPropertiesProvider.properties.appProperties.formats.apiUrl;
    return this.withContext(context =>
      this.http
        .get(`${contractApiUrl}/contracts/specifications/${specificationKey}`, { responseType: 'text', context })
        .pipe(map(contract => this.parseSpecificationContract(contract)))
    );
  }

  public getContractReference(referenceUrl: string): Observable<SpecificationContract> {
    return this.withContext(context =>
      this.http.get(referenceUrl, { responseType: 'text', context }).pipe(map(contract => this.parseSpecificationContract(contract)))
    );
  }

  private parseSpecificationContract(contract: string): SpecificationContract {
    return load(contract) as SpecificationContract;
  }
}
